import { useEffect, useState } from "react";
import { supabase } from "../../../lib/supabase";

export default function FacebookCallback() {
  const [status, setStatus] = useState("Processing Facebook connection...");
  const [error, setError] = useState(null);

  useEffect(() => {
    async function run() {
      console.log("[FacebookCallback] Starting callback processing...");

      const code = new URLSearchParams(window.location.search).get("code");
      const errorParam = new URLSearchParams(window.location.search).get("error");

      // Check for Facebook OAuth errors
      if (errorParam) {
        const errorMessage = new URLSearchParams(window.location.search).get("error_description");
        console.error("[FacebookCallback] Facebook OAuth error:", errorParam, errorMessage);
        setError(`Facebook OAuth error: ${errorMessage || errorParam}`);
        setStatus("Connection cancelled or failed");
        return;
      }

      if (!code) {
        console.error("[FacebookCallback] No authorization code received");
        setError("No authorization code received from Facebook");
        setStatus("Connection failed");
        return;
      }

      console.log("[FacebookCallback] Authorization code received");

      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          console.error("[FacebookCallback] No active session");
          setError("Your session has expired. Please sign in again.");
          setStatus("Session expired");
          return;
        }

        console.log("[FacebookCallback] User session found:", session.user?.id);

        // The backend facebook-callback function handles the OAuth exchange
        // and stores pages in the database
        console.log("[FacebookCallback] Calling backend facebook-callback function...");
        const tokenRes = await fetch(
          `${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/facebook-callback`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ code }),
          }
        );

        console.log("[FacebookCallback] Callback response status:", tokenRes.status);

        if (!tokenRes.ok) {
          const errorData = await tokenRes.json().catch(() => ({}));
          console.error("[FacebookCallback] Callback error:", errorData);
          throw new Error(errorData.error || "Failed to process Facebook callback");
        }

        setStatus("Facebook connected successfully! Redirecting...");

        // Redirect to the Facebook pages list
        console.log("[FacebookCallback] Redirecting to /fb/page-stats...");
        window.location.href = "/fb/page-stats";
      } catch (err) {
        console.error("[FacebookCallback] Error:", err);
        setError(err.message || "An unexpected error occurred");
        setStatus("Connection failed");
      }
    }

    run();
  }, []);

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center">
            <div className="text-4xl mb-4">❌</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Connection Failed</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <a
              href="/fb/page-stats"
              className="inline-block px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Back to Facebook Pages
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <div className="text-center">
          <div className="text-4xl mb-4">🔗</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Connecting Facebook</h2>
          <p className="text-gray-600 mb-4">{status}</p>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
